import { useTrack } from "@/hooks/useTrack";
import { useEffect, useRef } from "react";

const HEARTBEAT_MS = 30_000;

/**
 * Tracks session duration for a page/view; `properties` is spread into every event. Fires:
 * - `start` on mount
 * - `heartbeat` every 30 s while the tab is visible
 * - `end` on unmount
 */
export function useSessionTimer(event: string, properties: Record<string, unknown>) {
  const track = useTrack();
  const propsRef = useRef(properties);

  useEffect(() => {
    propsRef.current = properties;
  });

  useEffect(() => {
    track(event, { ...propsRef.current, action: "start" });

    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        track(event, { ...propsRef.current, action: "heartbeat" });
      }
    }, HEARTBEAT_MS);

    return () => {
      clearInterval(interval);
      track(event, { ...propsRef.current, action: "end" });
    };
    // Only re-run when the event name changes, not on every properties change.
    // Properties are read from the ref.
  }, [event, track]);
}
